import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "input", "results", "feedback"]
  static values = {
    albumId: Number,
    userAlbumId: Number,
    fetchUrl: String,
    addUrl: String,
  }

  connect() {
    this.boundKeydown = this._keydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
    this._debounceTimer = null
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    clearTimeout(this._debounceTimer)
  }

  _keydown(e) {
    if (e.key === "Escape") {
      this.close()
    }
    if ((e.key === "a" || e.key === "A") && !e.ctrlKey && !e.metaKey && !e.target.closest("input, textarea, select")) {
      e.preventDefault()
      this.open()
    }
  }

  keydown(e) {
    if (this.overlayTarget.classList.contains("hidden")) return

    if (e.key === "Escape") {
      this.close()
      return
    }

    if (e.key === "ArrowDown") {
      e.preventDefault()
      this._highlightNext()
      return
    }

    if (e.key === "ArrowUp") {
      e.preventDefault()
      this._highlightPrev()
      return
    }

    if (e.key === "Enter") {
      const highlighted = this.resultsTarget.querySelector("[data-highlighted]")
      if (highlighted) {
        e.preventDefault()
        highlighted.click()
      }
    }
  }

  open() {
    this.overlayTarget.classList.remove("hidden")
    setTimeout(() => this.inputTarget.focus(), 100)
    this.resultsTarget.innerHTML = `
      <div class="text-center text-sm text-stone-400 dark:text-gray-500 py-8">
        <i class="fa-solid fa-search text-2xl mb-2 block"></i>
        Digite o código ou nome da figurinha
      </div>
    `
  }

  close() {
    this.overlayTarget.classList.add("hidden")
    this.inputTarget.value = ""
    this.resultsTarget.innerHTML = ""
  }

  async search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(async () => {
      await this._doSearch()
    }, 150)
  }

  async _doSearch() {
    const q = this.inputTarget.value.trim()

    if (!q || q.length < 1) {
      this.resultsTarget.innerHTML = `
        <div class="text-center text-sm text-stone-400 dark:text-gray-500 py-8">
          <i class="fa-solid fa-search text-2xl mb-2 block"></i>
          Digite o código ou nome da figurinha
        </div>
      `
      return
    }

    const url = new URL(this.fetchUrlValue, window.location.origin)
    url.searchParams.set("q", q)

    try {
      const response = await fetch(url, {
        headers: { "Accept": "application/json" },
      })
      const stickers = await response.json()
      this._renderResults(stickers)
    } catch (error) {
      this.resultsTarget.innerHTML = `
        <div class="text-center text-sm text-red-500 py-4">
          Erro ao buscar figurinhas.
        </div>
      `
    }
  }

  _renderResults(stickers) {
    if (stickers.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="text-center text-sm text-stone-400 dark:text-gray-500 py-8">
          <i class="fa-solid fa-face-frown text-2xl mb-2 block"></i>
          Nenhuma figurinha encontrada
        </div>
      `
      return
    }

    const rarityIcons = {
      "normal": "",
      "shiny": "fa-gem text-cyan-500",
      "legend": "fa-fire text-amber-500",
    }

    this.resultsTarget.innerHTML = stickers.map((s, i) => `
      <button type="button"
              data-action="click->quick-add#add"
              data-sticker-number="${s.number}"
              data-index="${i}"
              class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${i === 0 ? 'bg-album-primary/10 dark:bg-blue-900/30' : 'hover:bg-stone-50 dark:hover:bg-gray-700'}"
              ${i === 0 ? 'data-highlighted' : ''}>
        <span class="font-mono font-bold text-sm text-album-primary w-16 shrink-0">${s.number}</span>
        <span class="flex-1 text-sm text-stone-700 dark:text-gray-200 truncate">${s.name || ""}</span>
        <span class="text-xs text-stone-400 dark:text-gray-500">${s.team}</span>
        ${s.rarity !== "normal" ? `<i class="fa-solid ${rarityIcons[s.rarity]} text-xs"></i>` : ""}
      </button>
    `).join("")
  }

  _highlightNext() {
    const items = this.resultsTarget.querySelectorAll("[data-index]")
    const current = this.resultsTarget.querySelector("[data-highlighted]")
    const nextIdx = current ? parseInt(current.dataset.index) + 1 : 0

    if (nextIdx < items.length) {
      this._setHighlight(items[nextIdx])
    }
  }

  _highlightPrev() {
    const items = this.resultsTarget.querySelectorAll("[data-index]")
    const current = this.resultsTarget.querySelector("[data-highlighted]")
    const prevIdx = current ? parseInt(current.dataset.index) - 1 : items.length - 1

    if (prevIdx >= 0) {
      this._setHighlight(items[prevIdx])
    }
  }

  _setHighlight(el) {
    this.resultsTarget.querySelectorAll("[data-highlighted]").forEach(item => {
      item.removeAttribute("data-highlighted")
      item.className = "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left hover:bg-stone-50 dark:hover:bg-gray-700"
    })
    el.setAttribute("data-highlighted", "")
    el.className = "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left bg-album-primary/10 dark:bg-blue-900/30"
    el.scrollIntoView({ block: "nearest" })
  }

  async add(event) {
    const number = event.currentTarget.dataset.stickerNumber
    if (!number) return

    this.inputTarget.value = number

    try {
      const response = await fetch(this.addUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({ number }),
      })

      if (response.ok) {
        const data = await response.json()
        this._updateFeedback(data)
        this._updateCardVisual(data)
        this.inputTarget.value = ""
        this.inputTarget.focus()
        this._doSearch()
      }
    } catch (error) {
      console.error("Quick add error:", error)
    }
  }

  _updateFeedback(data) {
    const s = data.sticker
    this.feedbackTarget.innerHTML = `
      <div class="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
        <i class="fa-solid fa-check-circle"></i>
        <span class="font-medium">${s.number}</span>
        <span class="text-stone-500 dark:text-gray-400">${s.name || ""}</span>
        <span class="ml-auto text-stone-400 dark:text-gray-500">${data.quantity}x</span>
      </div>
    `
    setTimeout(() => {
      this.feedbackTarget.innerHTML = ""
    }, 2000)
  }

  _updateCardVisual(data) {
    const card = document.querySelector(`[data-sticker-id="${data.sticker.id}"]`)
    if (!card) return

    card.dataset.stickerQuantity = data.quantity
    card.classList.remove("owned", "duplicate", "missing", "dup-2", "dup-3", "dup-4", "dup-5")

    if (data.quantity > 1) {
      card.classList.add("duplicate")
      card.classList.add(`dup-${Math.min(data.quantity, 5)}`)
    } else if (data.quantity === 1) {
      card.classList.add("owned")
    } else {
      card.classList.add("missing")
    }

    let controls = card.querySelector(".sticker-controls")
    if (data.quantity >= 1) {
      if (!controls) {
        controls = document.createElement("div")
        controls.className = "sticker-controls"

        const minusBtn = document.createElement("button")
        minusBtn.className = "sticker-btn"
        minusBtn.textContent = "\u2212"
        minusBtn.dataset.action = "click->sticker#decrement"

        const qtyEl = document.createElement("span")
        qtyEl.className = "sticker-qty"
        qtyEl.textContent = data.quantity

        const plusBtn = document.createElement("button")
        plusBtn.className = "sticker-btn"
        plusBtn.textContent = "+"
        plusBtn.dataset.action = "click->sticker#increment"

        controls.appendChild(minusBtn)
        controls.appendChild(qtyEl)
        controls.appendChild(plusBtn)
        card.appendChild(controls)
      } else {
        const qtyEl = controls.querySelector(".sticker-qty")
        if (qtyEl) qtyEl.textContent = data.quantity
      }
    } else {
      if (controls) controls.remove()
    }

    document.querySelectorAll(".progress-bar-fill").forEach(bar => {
      bar.style.width = `${data.progress}%`
    })

    document.querySelectorAll("[data-count-owned]").forEach(el => {
      el.textContent = data.owned
    })
    document.querySelectorAll("[data-count-duplicates]").forEach(el => {
      el.textContent = data.duplicates
    })
    document.querySelectorAll("[data-count-missing]").forEach(el => {
      el.textContent = data.missing
    })

    const teamSection = card.closest("[data-team]")
    if (teamSection) {
      const teamCounter = teamSection.querySelector("[data-team-counter]")
      if (teamCounter) {
        const total = teamCounter.textContent.split("/")[1]
        teamCounter.textContent = `${data.team_owned}/${total}`
      }
    }
  }

  _debounceTimer
}
